import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import "./TokenInputModal.css";

const TokenInputModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  spaceName,
  confirmText = "Verify & Delete",
  cancelText = "Cancel",
  loading = false,
  error = null,
  onResendToken = null,
  resendingToken = false,
  expiresAt = null,
}) => {
  const [token, setToken] = useState("");
  const [timeRemaining, setTimeRemaining] = useState(null);
  const inputRef = useRef(null);

  // Calculate time remaining
  useEffect(() => {
    if (!isOpen || !expiresAt) {
      setTimeRemaining(null);
      return;
    }

    const updateTimeRemaining = () => {
      const now = new Date();
      const expires = new Date(expiresAt);
      const diff = expires - now;

      if (diff <= 0) {
        setTimeRemaining(null);
        return;
      }

      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [isOpen, expiresAt]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset token when modal closes
  useEffect(() => {
    if (!isOpen) {
      setToken("");
    }
  }, [isOpen]);

  const handleTokenChange = (e) => {
    // Only allow alphanumeric characters, max 8 characters
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 8);
    setToken(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (token.length === 8 && !loading) {
      onConfirm(token);
    }
  };

  const handleHide = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleHide}
      centered
      backdrop={loading ? "static" : true}
      keyboard={!loading}
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message && <p className="token-modal-message">{message}</p>}

        <div className="token-modal-input-container">
          <label htmlFor="deletion-token-input" className="token-modal-label">
            Enter verification code sent to your email:
          </label>
          <form onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              id="deletion-token-input"
              type="text"
              value={token}
              onChange={handleTokenChange}
              placeholder="Enter 8-digit code"
              disabled={loading}
              maxLength={8}
              className={`token-modal-input ${error ? "error" : ""}`}
            />
          </form>
          {error && <p className="token-modal-error">{error}</p>}
          {timeRemaining && (
            <p className="token-modal-timer">
              Code expires in: <strong>{timeRemaining}</strong>
            </p>
          )}
        </div>

        {onResendToken && (
          <div className="token-modal-resend-container">
            <button
              type="button"
              onClick={onResendToken}
              disabled={resendingToken || loading}
              className="token-modal-resend-button"
            >
              {resendingToken ? "Sending..." : "Resend code"}
            </button>
          </div>
        )}

        <div className="token-modal-warning">
          <p className="token-modal-warning-text">
            <strong>Warning:</strong> Deleting "{spaceName}" will permanently
            delete all files in this Space. This action cannot be undone.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          className="btn-secondary btn-sm"
          onClick={handleHide}
          disabled={loading}
          style={{
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          {cancelText}
        </button>
        <button
          className="btn-danger btn-sm token-modal-footer-button"
          onClick={() => onConfirm(token)}
          disabled={loading || token.length !== 8}
        >
          {loading && <span className="token-modal-spinner" />}
          {loading ? "Verifying..." : confirmText}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default TokenInputModal;
